import Icon from "@client/controls/Icon/component";
import Image from "@client/controls/Image/container";
import TextInput, { type TextInputAutoComplete } from "@client/controls/Inputs/TextInput/component";
import withValueHOC from "@client/hocs/withValue";
import { IconType, type WithOnChange, type WithValue } from "@client/types";
import { useI18n } from "@common/hooks/i18n";
import classnames from "classnames";
import * as React from "react";
import "./style.less";

export type SearchMapProps = WithValue<string> & {
  autoComplete?: TextInputAutoComplete;
  autoFocus?: boolean;
  className?: string;
  iconPosition?: "left" | "right";
  onChangeDelay?: number;
  placeholder?: string;
  showEnter?: boolean;
  style?: React.CSSProperties;
};

export type SearchActionProps = WithOnChange<string> & {
  onBlur?: () => void;
  onClear?: () => void;
  onEnter?: () => void;
  onFocus?: () => void;
};

type SearchProps = SearchMapProps & SearchActionProps;
const SearchBase = ({
  className,
  iconPosition,
  showEnter,
  onClear,
  style,
  value,
  onChangeDelay,
  ...inputProps
}: SearchProps) => {
  const i18n = useI18n();

  const onClearClick = () => {
    inputProps.onChange("");
    onClear && onClear();
  };

  const onEscape = (e: React.KeyboardEvent) => {
    if (value) {
      e.stopPropagation();
      onClearClick();
    }
  };

  return (
    // form prevents autocomplete behaviour
    <form
      className={classnames("wsc_search_box", className, `icon-${iconPosition || "left"}`, {
        with_enter: showEnter
      })}
      style={style}
    >
      {value ? (
        <Icon className="wsc_clear_icon" iconClassName="far fa-times-circle" onClick={onClearClick} />
      ) : (
        <Icon className="wsc_search_icon" iconClassName={IconType.Search} />
      )}

      <TextInput
        {...inputProps}
        onEscape={onEscape}
        onChangeTrigger={onChangeDelay}
        placeholder={inputProps.placeholder ?? i18n.search}
        value={value}
      />

      {showEnter && (
        <small className="wsc_search_enter" onClick={inputProps.onEnter}>
          <span className="text">enter</span> <Image src={"#wsc-enter-arrow"} />
        </small>
      )}
    </form>
  );
};

const Search = withValueHOC<string, SearchProps>(SearchBase as any);
export default Search;

export type SearchControlledMapProps = Omit<SearchMapProps, "placeholder" | "value"> & {
  defaultValue: string;
};
export type SearchControlledActionProps = Omit<SearchActionProps, "onChange" | "onEnter"> & {
  onEnter: (text: string) => void;
};
export const SearchControlled = ({
  defaultValue,
  onEnter,
  ...props
}: SearchControlledActionProps & SearchControlledMapProps) => {
  const i18n = useI18n();
  const [value, setValue] = React.useState(defaultValue);

  React.useEffect(() => setValue(defaultValue), [defaultValue]);

  const _onEnter = React.useCallback(() => {
    onEnter && onEnter(value);
  }, [onEnter, value]);

  return (
    <Search {...props} placeholder={i18n.infoSearchInstructions} value={value} onEnter={_onEnter} onChange={setValue} />
  );
};
